/**
 * Site settings (clinic timing + logo).
 *
 * Consistency rules:
 * - The header NEVER renders a bundled/fallback logo. Until the real logo is
 *   known it renders an invisible spacer, so visitors never see a wrong logo
 *   being swapped out for the configured one.
 * - The server injects the current settings into index.html
 *   (window.__SITE_SETTINGS__) before sending it, so the very first paint
 *   already has the configured logo — no flash on any page load.
 * - localStorage keeps the last known logo as a secondary cache (covers
 *   index.html served from an older cache).
 * - The theme color is pure CSS (a single `--theme-color` in index.css).
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchJson } from './api';

export const LOGO_CACHE_KEY = 'site_logo_url';

const DEFAULTS = {
  logoUrl: null,
  logoLoaded: false,
  timing: { weekdays: '', weekend: '' },
};

const SiteSettingsCtx = createContext(DEFAULTS);
export const useSiteSettings = () => useContext(SiteSettingsCtx);

/** Remember the resolved logo so the next load paints it immediately. */
export function cacheLogo(url) {
  try {
    if (url) window.localStorage.setItem(LOGO_CACHE_KEY, url);
    else window.localStorage.removeItem(LOGO_CACHE_KEY);
  } catch (_) {
    /* storage unavailable (private mode) - ignore */
  }
}

/** Drop a cached logo that turned out to be broken/deleted. */
export function forgetCachedLogo() {
  cacheLogo(null);
}

function readCachedLogo() {
  try {
    return window.localStorage.getItem(LOGO_CACHE_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function SiteSettingsProvider({ children }) {
  // Settings the server embedded in index.html (available synchronously).
  const injected =
    (typeof window !== 'undefined' && window.__SITE_SETTINGS__) || null;

  const [settings, setSettings] = useState(() => {
    const logoUrl = injected ? injected.logoUrl || null : readCachedLogo();
    return {
      // An injected payload is authoritative: if it says "no logo", we are
      // loaded and simply have no logo to show (never a fallback image).
      logoUrl,
      logoLoaded: Boolean(injected) || Boolean(logoUrl),
      timing: (injected && injected.timing) || DEFAULTS.timing,
    };
  });

  useEffect(() => {
    let mounted = true;

    fetchJson('settings').then((data) => {
      if (!mounted) return;

      // Could not reach the API: keep what we have and stop waiting.
      if (!data || !data.settings) {
        setSettings((prev) => ({ ...prev, logoLoaded: true }));
        return;
      }

      const s = data.settings;
      const logoUrl = s.logo_url || null;

      setSettings({
        logoUrl,
        logoLoaded: true,
        timing: {
          weekdays: s.timing?.weekdays || '',
          weekend: s.timing?.weekend || '',
        },
      });

      cacheLogo(logoUrl);
    });

    return () => {
      mounted = false;
    };
  }, []);

  return <SiteSettingsCtx.Provider value={settings}>{children}</SiteSettingsCtx.Provider>;
}
